import {
  Space,
  Table,
  List,
  CreateButton,
  Drawer,
  Form,
  Input,
  Button,
  Grid,
  notification,
  Icons,
  Popconfirm,
} from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { IPaymentMethod } from 'interfaces';
import { EditPaymentMethod } from './EditPaymentMethod';
import { createPaymentMethod, deletePaymentMethod, getPaymentMethods } from 'services/payment';
import { capitalize } from 'utilities/shared';
import { useCan } from '@pankod/refine-core';

type PaymentMethodsTableProps = {
  resource: 'client' | 'supplier' | 'company';
  resourceId: string;
};

export const PaymentMethodsTable: React.FC<PaymentMethodsTableProps> = (props) => {
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const capitalizedResource = capitalize(props.resource);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const paymentMethods = await getPaymentMethods(props.resource, props.resourceId);
        setPaymentMethods(paymentMethods);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
      }
    })();
  }, [props.resource, props.resourceId]);

  const resource = window.location.pathname.split('/')[1].toLowerCase();
  const { data: canAccessDelete } = useCan({
    resource: resource,
    action: 'delete',
    params: { field: 'id' },
  });

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const onFinish = async (values: Omit<IPaymentMethod, 'id'>) => {
    setIsLoading(true);
    try {
      await createPaymentMethod(props.resource, values);
      const paymentMethods = await getPaymentMethods(props.resource, props.resourceId);
      setPaymentMethods(paymentMethods);
      notification.success({
        message: 'Success',
        description: 'Successfully created a payment method.',
      });
      form.resetFields();
      setVisible(false);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to create a payment method.',
      });
    }
    setIsLoading(false);
  };

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deletePaymentMethod(id, props.resource);
      const paymentMethods = await getPaymentMethods(props.resource, props.resourceId);
      setPaymentMethods(paymentMethods);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const titleProps = () => {
    return (
      <p style={{ fontSize: '16px', fontWeight: 'bold' }}>
        {capitalizedResource === 'Company'
          ? 'Bank Accounts'
          : `${capitalizedResource} Payment Methods`}
      </p>
    );
  };

  return (
    <>
      <List
        // title={
        //   capitalizedResource === 'Company'
        //     ? 'Bank Accounts'
        //     : `${capitalizedResource} Payment Methods`
        // }
        title={titleProps()}
        pageHeaderProps={{
          extra: (
            <CreateButton type="primary" onClick={showDrawer}>
              Add New
            </CreateButton>
          ),
        }}
      >
        <Table bordered dataSource={paymentMethods} rowKey="id" scroll={{ x: true }}>
          <Table.Column dataIndex="accountName" title="Account Name" />
          <Table.Column dataIndex="accountNumber" title="Account Number" />
          <Table.Column dataIndex="bankName" title="Bank Name" />
          <Table.Column dataIndex="branchName" title="Branch Name" />
          <Table.Column dataIndex="swiftCode" title="Swift Code" />
          <Table.Column<IPaymentMethod>
            dataIndex="actions"
            title="Actions"
            render={(_, pocRecord): React.ReactNode => {
              return (
                <Space>
                  <EditPaymentMethod
                    resource={props.resource}
                    resourceId={props.resourceId}
                    paymentMethod={pocRecord}
                    setPaymentMethods={setPaymentMethods}
                  />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={async () => {
                      await deleteConfirmHandler(pocRecord.id);
                    }}
                    disabled={!canAccessDelete?.can}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<Icons.DeleteOutlined />}
                      disabled={!canAccessDelete?.can}
                    />
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
      </List>
      <Drawer
        title="Add New Payment Method"
        placement="right"
        width={breakpoint.sm ? '450px' : '100%'}
        visible={visible}
        onClose={closeDrawer}
      >
        <Form
          layout="vertical"
          initialValues={{
            [props.resource]: props.resourceId,
            isActive: true,
          }}
          form={form}
          onFinish={onFinish}
        >
          <Form.Item
            label={`${props.resource} ID`}
            name={props.resource}
            rules={[
              {
                required: true,
              },
            ]}
            style={{ display: 'none' }}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item label="Account Name" name="accountName">
            <Input />
          </Form.Item>
          <Form.Item label="Account Number" name="accountNumber">
            <Input />
          </Form.Item>
          <Form.Item
            label="Bank Name"
            name="bankName"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Branch Name" name="branchName">
            <Input />
          </Form.Item>
          <Form.Item label="Swift Code" name="swiftCode">
            <Input />
          </Form.Item>
          <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};
